import { DefaultContext, FetchResult, MutationHookOptions, MutationResult, NoInfer, useMutation } from '@apollo/client';
import merge from 'lodash/merge';

import {
  apolloClient,
  UpdateAscProjectMutation,
  UpdateAscProjectMutationVariables,
  UPDATE_ASC_PROJECT,
  AscProjectFragment,
  AscProjectFragmentDocument,
  UpdateAscProjectInput,
  PeriodName,
  updateWithInputObject,
} from '@neo.tax/shared';

import { AscProject } from './useAscProjects';

type UseUpdateAscProjectProps<TContext = DefaultContext> = {
  context?: TContext;
  ascProject: AscProject;
  /**
   * Only include the optimistic response when we are NOT doing real-time updates. Why?
   * Real-time updates often exhibit a delay when including the optimistic response, and we don't
   * want a janky UI/UX, so opt in when it makes the most sense.
   */
  includeOptimisticResponse?: boolean;
  fiscalYearId: string;
  periodName: PeriodName;
};

interface UpdateProjectOptions extends MutationHookOptions<
  NoInfer<UpdateAscProjectMutation>,
  NoInfer<UpdateAscProjectMutationVariables>,
  DefaultContext
> {
  /**
   * Only include the optimistic response when we are NOT doing real-time updates. Why?
   * Real-time updates often exhibit a delay when including the optimistic response, and we don't
   * want a janky UI/UX, so opt in when it makes the most sense.
   */
  includeOptimisticResponse?: boolean;
}

type UseUpdateAscProject = [
  (
    updateAscProjectInput: UpdateAscProjectInput,
    options?: UpdateProjectOptions,
  ) => Promise<FetchResult<UpdateAscProjectMutation>>,
  MutationResult<UpdateAscProjectMutation>,
];

export const useUpdateAscProject = ({
  ascProject,
  context: contextFromInit,
  fiscalYearId,
  includeOptimisticResponse: includeOptimisticResponseFromInit,
  periodName,
}: UseUpdateAscProjectProps): UseUpdateAscProject => {
  const [updateAscProjectMutation, updateAscProjectMutationResult] = useMutation<
    UpdateAscProjectMutation,
    UpdateAscProjectMutationVariables
  >(UPDATE_ASC_PROJECT, {
    context: {
      successToast: {
        title: 'Project updated successfully',
      },
      errorToast: {
        title: 'Error updating project',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
      },
      ...contextFromInit,
    },
  });

  const updateAscProject = (
    updateAscProjectInput: UpdateAscProjectInput,
    options: UpdateProjectOptions = {},
  ): Promise<FetchResult<UpdateAscProjectMutation>> => {
    const { includeOptimisticResponse, ...otherOptions } = merge(
      {},
      {
        context: contextFromInit,
        includeOptimisticResponse: includeOptimisticResponseFromInit,
      },
      options,
    );

    let optimisticResponse: UpdateAscProjectMutation | undefined;

    if (includeOptimisticResponse) {
      const ascProjectFromCache = apolloClient.readFragment<AscProjectFragment>({
        id: `AscProject:${ascProject.id}`,
        fragment: AscProjectFragmentDocument,
        fragmentName: 'AscProject',
        variables: { fiscalYearId, periodName },
      });
      optimisticResponse = ascProjectFromCache
        ? {
            __typename: 'Mutation',
            updateAscProject: updateWithInputObject(ascProjectFromCache, updateAscProjectInput),
          }
        : undefined;
    }

    return updateAscProjectMutation({
      variables: {
        ascProjectId: ascProject.id,
        updateAscProjectInput,
        fiscalYearId,
        periodName,
      },
      optimisticResponse,
      ...otherOptions,
    });
  };

  return [updateAscProject, updateAscProjectMutationResult];
};
